import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/AuthContext";
import { toast } from "sonner";
import { Plus, X, TreeStructure, PencilSimple, Trash, Copy, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function Organizations() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const reload = () => api.get("/organizations").then(r => setItems(r.data));
  useEffect(() => { reload(); }, []);

  const toggle = async (id) => {
    try { await api.patch(`/organizations/${id}/toggle`); toast.success("Actualizado"); reload(); }
    catch { toast.error("Error"); }
  };
  const duplicate = async (id) => {
    try { const r = await api.post(`/organizations/${id}/duplicate`); toast.success("Duplicada"); reload(); nav(`/organizations/${r.data.id}`); }
    catch { toast.error("Error al duplicar"); }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Eliminar la estructura "${name}" y todos sus nodos?`)) return;
    try { await api.delete(`/organizations/${id}`); toast.success("Eliminada"); reload(); }
    catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-6" data-testid="organizations-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Super Admin</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Estructuras organizacionales</h1>
          <p className="text-neutral-500 mt-2 max-w-2xl">
            Construye jerarquías reutilizando empresas y sucursales existentes. Una empresa puede
            participar en varias estructuras sin duplicarse.
          </p>
        </div>
        <button onClick={()=>setOpen(true)} className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md" data-testid="new-structure-btn">
          <Plus size={18} weight="bold"/> Nueva estructura
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border-2 border-dashed border-neutral-300 rounded-md p-12 text-center text-neutral-500" data-testid="empty-structures">
          <TreeStructure size={48} weight="duotone" className="mx-auto mb-3 text-neutral-400"/>
          <p className="font-semibold text-neutral-700">Aún no hay estructuras</p>
          <p className="text-sm">Crea tu primera estructura organizacional para empezar.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {items.map(s => (
            <div key={s.id} className="bg-white border border-neutral-200 rounded-md p-5 flex flex-col gap-3 hover:border-neutral-300 transition-colors" data-testid={`structure-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-neutral-900 truncate">{s.name}</div>
                  {s.description && <div className="text-xs text-neutral-500 line-clamp-2 mt-0.5">{s.description}</div>}
                </div>
                {s.active ? (
                  <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded border border-emerald-200">
                    <CheckCircle size={12} weight="fill"/> Activa
                  </span>
                ) : (
                  <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-neutral-100 text-neutral-500 text-xs font-semibold rounded border border-neutral-200">
                    <XCircle size={12} weight="fill"/> Inactiva
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1">
                {(s.levels || []).map(l => (
                  <span key={l.number} className="px-2 py-0.5 bg-neutral-100 text-neutral-700 text-[11px] rounded border border-neutral-200">
                    L{l.number} · {l.label}
                  </span>
                ))}
              </div>
              <div className="flex gap-2 mt-auto pt-2 border-t border-neutral-100">
                <Link to={`/organizations/${s.id}`} className="flex-1 text-center text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold px-3 py-1.5 rounded-md" data-testid={`open-structure-${s.id}`}>
                  Abrir
                </Link>
                <button onClick={()=>toggle(s.id)} className="text-xs text-neutral-500 hover:text-neutral-800 px-2" data-testid={`toggle-structure-${s.id}`}>
                  {s.active ? "Desactivar" : "Activar"}
                </button>
                <button onClick={()=>duplicate(s.id)} className="text-neutral-400 hover:text-neutral-700" title="Duplicar" data-testid={`duplicate-structure-${s.id}`}>
                  <Copy size={16}/>
                </button>
                <button onClick={()=>remove(s.id, s.name)} className="text-rose-400 hover:text-rose-600" title="Eliminar" data-testid={`delete-structure-${s.id}`}>
                  <Trash size={16}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && <StructureForm onClose={()=>setOpen(false)} onCreated={(id)=>{ setOpen(false); nav(`/organizations/${id}`); }}/>}
    </div>
  );
}

function StructureForm({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [levels, setLevels] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/organizations/level-catalog").then(r => {
      setCatalog(r.data);
      setLevels(r.data.map(l => l.number));  // selección por defecto: todos
    });
  }, []);

  const toggleLevel = (num) => {
    setLevels(prev => prev.includes(num) ? prev.filter(n => n !== num) : [...prev, num].sort((a,b)=>a-b));
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Nombre requerido"); return; }
    if (levels.length === 0) { toast.error("Selecciona al menos un nivel"); return; }
    setBusy(true);
    try {
      const r = await api.post("/organizations", { name: name.trim(), description, level_numbers: levels, active: true });
      toast.success("Estructura creada");
      onCreated(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-structure-modal">
      <div className="bg-white rounded-md w-full max-w-xl border border-neutral-200 anim-fade-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <h2 className="font-display text-xl font-bold tracking-tight">Nueva estructura</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid="new-structure-close"><X size={20}/></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Nombre</label>
            <input value={name} onChange={e=>setName(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 rounded-md" data-testid="new-structure-name" placeholder="Ej. Operación Nacional"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Descripción</label>
            <textarea value={description} onChange={e=>setDescription(e.target.value)} rows={2} className="w-full px-3 py-2 border border-neutral-300 rounded-md" data-testid="new-structure-desc" placeholder="Opcional"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Niveles a incluir</label>
            <div className="space-y-1.5">
              {catalog.map(l => (
                <label key={l.number} className="flex items-center gap-2 px-3 py-2 border border-neutral-200 rounded-md cursor-pointer hover:bg-neutral-50">
                  <input type="checkbox" checked={levels.includes(l.number)} onChange={()=>toggleLevel(l.number)} data-testid={`level-check-${l.number}`}/>
                  <span className="font-mono text-xs text-neutral-500 w-8">L{l.number}</span>
                  <span className="font-semibold">{l.label}</span>
                  {l.ref_type && <span className="text-xs text-neutral-500">· vincula {l.ref_type === "company" ? "empresas" : "instalaciones (sucursales)"}</span>}
                </label>
              ))}
            </div>
            <p className="text-xs text-neutral-500 mt-2">Los niveles 4 y 5 quedan reservados para expansión futura.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md hover:bg-white">Cancelar</button>
          <button onClick={submit} disabled={busy} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="new-structure-submit">
            {busy ? "Creando..." : "Crear estructura"}
          </button>
        </div>
      </div>
    </div>
  );
}
